import { useEffect, useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useCRUD } from "../../api/crud";
import { PARTS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import { DECIMAL_INPUT_REGEX, formatDecimalForInput, toApiDecimal } from "../../utils";

interface PartFormValues {
  name: string;
  unit: string;
  code: string;
  notification_threshold: string;
}

const defaultValues: PartFormValues = {
  name: "",
  unit: "",
  code: "",
  notification_threshold: "",
};

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

interface PartFormProps {
  id?: string;
  onSuccess?: (data?: any) => void;
}

export default function PartForm({ id, onSuccess }: PartFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, update, loading } = useCRUD(PARTS_ENDPOINT);
  const [formData, setFormData] = useState<PartFormValues>(defaultValues);
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [loadingInitial, setLoadingInitial] = useState(!!id);

  useEffect(() => {
    if (!id) {
      setLoadingInitial(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const response = await get(id);
      if (cancelled) return;
      if (response?.data) {
        const d = response.data;
        setFormData({
          name: d.name ?? "",
          unit: d.unit ?? "",
          code: d.code ?? "",
          notification_threshold: formatDecimalForInput(d.notification_threshold),
        });
      }
      setLoadingInitial(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, get]);

  const getFieldError = (field: keyof PartFormValues): string | null =>
    extractErrorMessage(serverErrors?.[field]);

  const handleChange = (field: keyof PartFormValues) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleDecimalChange = (field: keyof PartFormValues) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = e.target.value;
    if (nextValue === "" || DECIMAL_INPUT_REGEX.test(nextValue)) {
      setFormData((prev) => ({ ...prev, [field]: nextValue }));
    }
  };

  const handleSubmit = async () => {
    setServerErrors(null);
    const payload = {
      name: formData.name,
      unit: formData.unit,
      code: formData.code || undefined,
      notification_threshold: formData.notification_threshold
        ? toApiDecimal(formData.notification_threshold)
        : undefined,
    };

    const response = id ? await update({ id, ...payload }) : await create(payload);

    if (response?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(response.data);
    } else {
      setServerErrors(response?.errorData);
      const failureMessage =
        extractErrorMessage(response?.errorData?.message) ||
        extractErrorMessage(response?.errorData?.detail) ||
        t("There was an error, please review the form.");
      enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
    }
  };

  if (loadingInitial) return <CustomCircularProgress />;

  const fieldBoxSx = {
    flex: { xs: "1 1 100%", sm: "1 1 45%" },
    maxWidth: { xs: "100%", sm: "calc(50% - 8px)" },
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Name")}
          value={formData.name}
          onChange={handleChange("name")}
          required
          fullWidth
          inputProps={{ maxLength: 200 }}
          error={Boolean(getFieldError("name"))}
          helperText={getFieldError("name") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Unit")}
          value={formData.unit}
          onChange={handleChange("unit")}
          required
          fullWidth
          inputProps={{ maxLength: 20 }}
          error={Boolean(getFieldError("unit"))}
          helperText={getFieldError("unit") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Code")}
          value={formData.code}
          onChange={handleChange("code")}
          fullWidth
          inputProps={{ maxLength: 100 }}
          error={Boolean(getFieldError("code"))}
          helperText={getFieldError("code") || ""}
        />
      </Box>
      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Notification Threshold")}
          value={formData.notification_threshold}
          onChange={handleDecimalChange("notification_threshold")}
          fullWidth
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          error={Boolean(getFieldError("notification_threshold"))}
          helperText={getFieldError("notification_threshold") || ""}
        />
      </Box>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
